<template>
  <div class="container-fluid">
    <div class="row mb-5">
      <div class="col-8">
        <span class="display-4 col-12">{{ $t("COMMON.DETAILS") }}</span>
      </div>
      <div class="col-4 text-right" v-if="version == 1">
        <base-button
          @click="openSalesOrderItemModal()"
          type="button"
          class="btn btn-sm btn-primary"
          v-if="salesOrder.status === ORDER_STATUS_DRAFT"
        >
          <span class="btn-inner--icon"><i class="fas fa-plus"></i></span>
          {{ $t("SALES_ORDERS.ADD_SALES_ORDER_ITEM") }}
        </base-button>
      </div>
    </div>

    <template v-if="salesOrder.id">
      <sales-order-view-details-sales-order-item-table-v1
        v-if="version == 1"
        :salesOrder="salesOrder"
        @onOpenSalesOrderItemModal="openSalesOrderItemModal"
      />

      <sales-order-view-details-sales-order-item-table-v2
        v-if="version == 2"
        :salesOrder="salesOrder"
        @onOpenSalesOrderItemModal="openSalesOrderItemModal"
        @onSubmitSalesOrderItemForm="handleSalesOrderItemFormSubmit"
        @onDeleteSalesOrderItem="deleteSalesOrderItem"
        @onSalesOrderUpdated="handleSalesOrderUpdated"
      />
    </template>

    <div class="row mb-5" v-if="salesOrder.pricing">
      <div class="col-6"></div>
      <div class="col-6">
        <dl class="row mb-0">
          <dt class="col-sm-6">{{ $t("COMMON.SUBTOTAL") }}</dt>
          <dd class="col-sm-6 text-right px-0">
            {{ $formatCurrency(salesOrder.pricing.subtotal) }}
          </dd>
        </dl>

        <dl class="row mb-0" v-if="salesOrder.pricing.discounts.amount">
          <dt class="col-sm-6">{{ $t("COMMON.DISCOUNTS") }}</dt>
          <dd class="col-sm-6 text-right px-0">
            {{ $formatCurrency(salesOrder.pricing.discounts.amount * -1) }}
          </dd>
          <div class="col-12">
            <dl
              class="row mb-0"
              v-for="(
                discount, index
              ) in salesOrder.pricing.discounts.details.filter(
                (discount) => discount.amount != 0
              )"
              v-bind:key="index"
            >
              <dt class="col-sm-6 pl-5 text-muted">
                {{
                  $te(`COMMON.${discount.name}`)
                    ? $t(`COMMON.${discount.name}`)
                    : discount.name
                }}
                (-{{
                  discount.type == "PERCENTAGE"
                    ? discount.value + "%"
                    : $formatCurrency(discount.value)
                }})
              </dt>
              <dd class="col-sm-6 text-right text-muted">
                {{ $formatCurrency(discount.amount * -1) }}
              </dd>
            </dl>
          </div>
        </dl>

        <dl v-if="salesOrder.pricing.discounts.amount != 0" class="row mb-0">
          <dt class="col-sm-6">{{ $t("COMMON.DISCOUNTED_SUBTOTAL") }}</dt>
          <dd class="col-sm-6 text-right px-0">
            {{ $formatCurrency(salesOrder.pricing.discounted_subtotal) }}
          </dd>
        </dl>

        <dl class="row mb-0" v-if="salesOrder.pricing.taxes.total">
          <dt class="col-sm-6">{{ $t("COMMON.TAXES") }}</dt>
          <dd class="col-sm-6 text-right px-0">
            {{ $formatCurrency(salesOrder.pricing.taxes.total) }}
          </dd>
          <div class="col-12">
            <dl
              class="row mb-0 px-0 mx-0"
              v-for="(tax, index) in salesOrder.pricing.taxes.taxes"
              v-bind:key="index"
            >
              <dt class="col-sm-6 pl-5 text-muted">{{ tax.name }}</dt>
              <dd class="col-sm-6 text-right text-muted px-0">
                {{ $formatCurrency(tax.tax_amount) }}
              </dd>
            </dl>
          </div>
        </dl>

        <!-- Container deposits: excluded from subtotal, discounts and taxes above. -->
        <dl class="row mb-0" v-if="salesOrder.pricing.deposits_total">
          <dt class="col-sm-6">{{ $t("COMMON.DEPOSITS") }}</dt>
          <dd class="col-sm-6 text-right px-0">
            {{ $formatCurrency(salesOrder.pricing.deposits_total) }}
          </dd>
        </dl>

        <dl class="row mb-0">
          <dt class="col-sm-6">{{ $t("COMMON.TOTAL") }}</dt>
          <dd class="col-sm-6 text-right px-0">
            {{ $formatCurrency(salesOrder.pricing.total) }}
          </dd>
        </dl>
      </div>
    </div>

    <!-- Modal add slot -->
    <sales-order-view-details-sales-order-item-form
      :salesOrder="salesOrder"
      :salesOrderItem="salesOrderItem"
      :showModal="showModal"
      :formErrors="itemsFormError"
      @onCloseSalesOrderItemModal="closeSalesOrderItemModal"
      @onSubmitSalesOrderItemForm="handleSalesOrderItemFormSubmit"
      @onDeleteSalesOrderItem="deleteSalesOrderItem"
    />
  </div>
</template>

<script>
import { Button, Tooltip } from "element-ui";
import { cloneDeep } from "lodash";
import formMixin from "@/mixins/form-mixin";
import { ORDER_STATUS_CANCELED, ORDER_STATUS_DRAFT } from "@/constants/orders";
import SalesOrderViewDetailsSalesOrderItemForm from "./SalesOrderViewDetailsSalesOrderItemForm.vue";
import SalesOrderViewDetailsSalesOrderItemTableV1 from "./SalesOrderViewDetailsSalesOrderItemTableV1.vue";
import SalesOrderViewDetailsSalesOrderItemTableV2 from "./SalesOrderViewDetailsSalesOrderItemTableV2.vue";
import defaultSalesOrderItem from "../defaultSalesOrderItem";
import { PRICING_TYPE_COUPONS } from "@/constants/pricingTypes";

export default {
  name: "sales-order-view-details",

  components: {
    SalesOrderViewDetailsSalesOrderItemForm,
    SalesOrderViewDetailsSalesOrderItemTableV1,
    SalesOrderViewDetailsSalesOrderItemTableV2,
    [Tooltip.name]: Tooltip,
    [Button.name]: Button,
  },

  mixins: [formMixin],

  props: {
    showHeader: {
      default: true,
    },
    salesOrder: {
      default: {},
    },
  },

  data() {
    return {
      salesOrderItem: cloneDeep(defaultSalesOrderItem),
      loading: false,
      showModal: false,
      ORDER_STATUS_DRAFT: ORDER_STATUS_DRAFT,
      ORDER_STATUS_CANCELED: ORDER_STATUS_CANCELED,
      itemsFormError: null,
    };
  },

  computed: {
    // La grille produits (v2) suppose une ligne rattachée à un produit : une
    // soumission de coupons porte une ligne libre, on garde donc la table v1.
    version() {
      return this.salesOrder?.pricing_type === PRICING_TYPE_COUPONS ? 1 : 2;
    },
  },

  methods: {
    openSalesOrderItemModal(salesOrderItem = null) {
      if (!salesOrderItem) {
        this.salesOrderItem = cloneDeep(defaultSalesOrderItem);
      } else {
        this.salesOrderItem = cloneDeep(salesOrderItem);
      }
      this.showModal = true;
    },

    closeSalesOrderItemModal() {
      this.showModal = false;
      this.loading = false;
    },

    handleSalesOrderItemFormSubmit(salesOrderItemData) {
      if (!salesOrderItemData.id) {
        this.addSalesOrderItem(salesOrderItemData);
      } else {
        this.editSalesOrderItem(salesOrderItemData);
      }
    },

    async addSalesOrderItem(salesOrderItemData) {
      this.loading = true;
      try {
        salesOrderItemData.salesOrder.id = this.salesOrder.id;

        await this.$store.dispatch("salesOrderItems/add", salesOrderItemData);
        this.$emit("salesOrderItemsUpdated", null);

        this.$notify({
          type: "success",
          message: this.$t("SALES_ORDERS.SALES_ORDER_ITEM_ADDED"),
        });

        this.loading = false;
        this.showModal = false;
      } catch (error) {
        this.$notify({
          type: "danger",
          message: this.$t("ERRORS.SOMETHING_WENT_WRONG"),
        });
        this.itemsFormError = error.response.data.errors;
        this.loading = false;
      }
    },

    async editSalesOrderItem(salesOrderItemData) {
      this.loading = true;
      try {
        await this.$store.dispatch(
          "salesOrderItems/update",
          salesOrderItemData
        );
        this.$emit("salesOrderItemsUpdated", null);

        this.$notify({
          type: "success",
          message: this.$t("SALES_ORDERS.SALES_ORDER_ITEM_EDITED"),
        });

        this.loading = false;
        this.showModal = false;
      } catch (error) {
        this.$notify({
          type: "danger",
          message: this.$t("ERRORS.SOMETHING_WENT_WRONG"),
        });
        this.itemsFormError = error.response.data.errors;
        this.loading = false;
      }
    },

    async deleteSalesOrderItem(id) {
      this.loading = true;
      try {
        await this.$store.dispatch("salesOrderItems/destroy", id);
        this.$emit("salesOrderItemsUpdated", null);

        this.$notify({
          type: "success",
          message: this.$t("SALES_ORDERS.SALES_ORDER_ITEM_DELETED"),
        });

        this.loading = false;
        this.showModal = false;
      } catch (error) {
        this.$notify({
          type: "danger",
          message: this.$t("ERRORS.SOMETHING_WENT_WRONG"),
        });
        this.loading = false;
      }
    },

    handleSalesOrderUpdated() {
      this.$emit("onSalesOrderUpdated", this.salesOrder);
    },
  },

  mounted() {},

  watch: {},
};
</script>
